import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DatePicker, TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import 'dayjs/locale/en-au';

import { StatusOption } from './useListJobActions';
import { LocationNodeData } from '../../@types/live-search.type';
import { useSearchAllocationNodes } from '../../queries/live-search-query';

const ACCENT_SX = { bgcolor: 'v4.accent', '&:hover': { bgcolor: 'v4.accent', opacity: 0.9 } };

export const ErrorText: React.FC<{ error: string | null }> = ({ error }) => {
  if (!error) return null;
  return <Typography sx={{ color: 'error.main', fontSize: '12px', mt: 1 }}>{error}</Typography>;
};

/* ---- Status Dialog ---- */

export const StatusDialog: React.FC<{
  open: boolean; isBulk: boolean; count: number;
  options: StatusOption[]; loading: boolean; submitting: boolean;
  lastError: string | null; onClose: () => void;
  onSubmit: (code: string, notes: string) => Promise<void>;
}> = ({ open, isBulk, count, options, loading, submitting, lastError, onClose, onSubmit }) => {
  const [selected, setSelected] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => { if (open) { setSelected(''); setNotes(''); } }, [open]);

  const handleSubmit = useCallback(() => {
    if (selected) onSubmit(selected, notes);
  }, [selected, notes, onSubmit]);

  const title = isBulk ? `Update Status (${count} jobs)` : 'Update Status';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-label={title}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : options.length === 0 ? (
          <Typography sx={{ color: 'text.secondary', py: 2 }}>No available status transitions.</Typography>
        ) : (
          <RadioGroup value={selected} onChange={(e) => setSelected(e.target.value)} aria-label="Status options">
            {options.map((opt) => (
              <FormControlLabel key={opt.code} value={opt.code} control={<Radio />} label={opt.name} />
            ))}
          </RadioGroup>
        )}
        <TextField
          label="Notes" multiline minRows={2} maxRows={4} fullWidth
          value={notes} onChange={(e) => setNotes(e.target.value)}
          sx={{ mt: 2 }} aria-label="Status change notes"
        />
        <ErrorText error={lastError} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} aria-label="Cancel status update">Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!selected || submitting}
          sx={ACCENT_SX} aria-label="Submit status update"
        >
          {submitting ? <CircularProgress size={18} color="inherit" /> : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ---- Assign Dialog ---- */

export const AssignDialog: React.FC<{
  open: boolean; isBulk: boolean; count: number;
  submitting: boolean; lastError: string | null; onClose: () => void;
  onSubmit: (nodeId: number, typeCode: string) => Promise<void>;
}> = ({ open, isBulk, count, submitting, lastError, onClose, onSubmit }) => {
  const searchMutation = useSearchAllocationNodes();
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<LocationNodeData[]>([]);
  const [selectedNode, setSelectedNode] = useState<LocationNodeData | null>(null);

  useEffect(() => { if (open) { setInputValue(''); setOptions([]); setSelectedNode(null); } }, [open]);

  useEffect(() => {
    if (inputValue.length < 2) { setOptions([]); return; }
    const timer = setTimeout(() => {
      searchMutation.mutateAsync(inputValue).then(setOptions).catch(() => setOptions([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const handleSubmit = useCallback(() => {
    if (selectedNode) onSubmit(selectedNode.id, 'primary');
  }, [selectedNode, onSubmit]);

  const title = isBulk ? `Assign Technician (${count} jobs)` : 'Assign Technician';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-label={title}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Autocomplete
          size="small" options={options} getOptionLabel={(opt) => opt.name}
          filterOptions={(x) => x} inputValue={inputValue}
          onInputChange={(_, val) => setInputValue(val)}
          onChange={(_, node) => setSelectedNode(node)}
          loading={searchMutation.isPending} sx={{ mt: 1 }}
          renderInput={(params) => (
            <TextField {...params} label="Search technician" placeholder="Type to search..." aria-label="Search technician" />
          )}
          renderOption={(props, option) => (
            <li {...props} key={`${option.id}-${option.code}`}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{option.name}</Typography>
                <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                  {option.nodeTypeName} &middot; {option.code}
                </Typography>
              </Box>
            </li>
          )}
        />
        <ErrorText error={lastError} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} aria-label="Cancel assign">Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!selectedNode || submitting}
          sx={ACCENT_SX} aria-label="Submit technician assignment"
        >
          {submitting ? <CircularProgress size={18} color="inherit" /> : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ---- Schedule Dialog ---- */

export const ScheduleDialog: React.FC<{
  open: boolean; isBulk: boolean; count: number;
  submitting: boolean; lastError: string | null;
  onClose: () => void; onSubmit: (date: string) => Promise<void>;
}> = ({ open, isBulk, count, submitting, lastError, onClose, onSubmit }) => {
  const [draftDate, setDraftDate] = useState<dayjs.Dayjs | null>(null);
  const [draftTime, setDraftTime] = useState<dayjs.Dayjs | null>(null);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (open) { setDraftDate(null); setDraftTime(dayjs().hour(9).minute(0)); setValidationError(''); }
  }, [open]);

  const handleSubmit = useCallback(() => {
    if (!draftDate || !draftDate.isValid()) { setValidationError('Please enter a valid date'); return; }
    if (!draftTime || !draftTime.isValid()) { setValidationError('Please enter a valid time'); return; }
    const combined = draftDate.hour(draftTime.hour()).minute(draftTime.minute()).second(0);
    onSubmit(combined.toISOString());
  }, [draftDate, draftTime, onSubmit]);

  const title = isBulk ? `Schedule Job (${count} jobs)` : 'Schedule Job';
  const isValid = draftDate?.isValid() && draftTime?.isValid();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth aria-label={title}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-au">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <DatePicker label="Date" value={draftDate}
              onChange={(val) => { setDraftDate(val); setValidationError(''); }}
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
            <TimePicker label="Time" value={draftTime}
              onChange={(val) => { setDraftTime(val); setValidationError(''); }}
              ampm={true}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </Box>
        </LocalizationProvider>
        {validationError && <Typography sx={{ color: 'error.main', fontSize: '12px', mt: 1 }}>{validationError}</Typography>}
        <ErrorText error={lastError} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} aria-label="Cancel schedule">Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!isValid || submitting}
          sx={ACCENT_SX} aria-label="Submit schedule"
        >
          {submitting ? <CircularProgress size={18} color="inherit" /> : 'Schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ---- Note Dialog ---- */

export const NoteDialog: React.FC<{
  open: boolean; isBulk: boolean; count: number;
  submitting: boolean; lastError: string | null;
  onClose: () => void; onSubmit: (note: string) => Promise<void>;
}> = ({ open, isBulk, count, submitting, lastError, onClose, onSubmit }) => {
  const [note, setNote] = useState('');
  useEffect(() => { if (open) setNote(''); }, [open]);
  const title = isBulk ? `Add Note (${count} jobs)` : 'Add Note';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-label={title}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField label="Note" multiline minRows={3} maxRows={6} fullWidth
          value={note} onChange={(e) => setNote(e.target.value)}
          sx={{ mt: 1 }} aria-label="Note content"
        />
        <ErrorText error={lastError} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} aria-label="Cancel add note">Cancel</Button>
        <Button variant="contained" onClick={() => onSubmit(note)}
          disabled={!note.trim() || submitting} sx={ACCENT_SX} aria-label="Submit note"
        >
          {submitting ? <CircularProgress size={18} color="inherit" /> : 'Add Note'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
